// src/components/ui/cards/TeamMemberCard/TeamMemberCardShadcn.jsx
import React from 'react';
import {
  Crown,
  Shield,
  User,
  Mail,
  Clock,
  Circle,
  X,
  RefreshCw,
  AlertTriangle
} from 'lucide-react';

// shadcn components
import { Card, CardContent } from '../../card';
import { Button } from '../../button';
import { Badge } from '../../badge';
import { Avatar, AvatarFallback } from '../../avatar';

import { cn } from '../../../../utils/cn';

const TeamMemberCardShadcn = ({
  member,
  type = 'member',
  isSelected = false,
  onClick,
  onCancelInvitation,
  onResendInvitation,
  canManageInvitations = false
}) => {
  // Role configuration
  const getRoleConfig = (role) => {
    switch (role) {
      case 'admin':
        return {
          icon: Crown,
          label: 'Administrator',
          variant: 'default',
          className: 'bg-amber-500/20 text-amber-300 border-amber-500/30'
        };
      case 'manager':
        return {
          icon: Shield,
          label: 'Manager',
          variant: 'secondary',
          className: 'bg-purple-500/20 text-purple-300 border-purple-500/30'
        };
      case 'staff':
        return {
          icon: User,
          label: 'Staff',
          variant: 'outline',
          className: 'bg-blue-500/20 text-blue-300 border-blue-500/30'
        };
      default:
        return {
          icon: User,
          label: 'Staff',
          variant: 'outline',
          className: 'bg-slate-500/20 text-slate-300 border-slate-500/30'
        };
    }
  };

  // Online status based on last_seen_at
  const getOnlineStatus = (lastSeen) => {
    if (!lastSeen) return { status: 'offline', color: 'bg-slate-500' };

    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = (now - lastSeenDate) / (1000 * 60);

    if (diffMinutes < 5) {
      return { status: 'online', color: 'bg-emerald-400' };
    } else if (diffMinutes < 30) {
      return { status: 'away', color: 'bg-amber-400' };
    } else {
      return { status: 'offline', color: 'bg-slate-500' };
    }
  };

  // Format last seen time
  const formatLastSeen = (lastSeen) => {
    if (!lastSeen) return 'Never';

    const lastSeenDate = new Date(lastSeen);
    const now = new Date();
    const diffMinutes = Math.floor((now - lastSeenDate) / (1000 * 60));

    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes}m ago`;

    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;

    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;

    return lastSeenDate.toLocaleDateString();
  };

  // Generate initials for avatar
  const getInitials = (name) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Format invitation date
  const formatInvitationDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  // Get days until expiration
  const getDaysUntilExpiration = (expiresAt) => {
    if (!expiresAt) return null;
    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffDays = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Generate initials from email for invitations
  const getEmailInitials = (email) => {
    return email
      .split('@')[0]
      .slice(0, 2)
      .toUpperCase();
  };

  const roleConfig = getRoleConfig(member.role);
  const RoleIcon = roleConfig.icon;
  const onlineStatus = getOnlineStatus(member.last_seen_at);

  // Render pending invitation version
  if (type === 'invitation') {
    const daysUntilExpiry = getDaysUntilExpiration(member.expires_at);
    const isExpired = member.is_expired;
    const isExpiringSoon = daysUntilExpiry !== null && daysUntilExpiry <= 2;

    return (
      <Card
        className={cn(
          "relative transition-all duration-300",
          isExpired
            ? "border-red-500/40 bg-red-500/10"
            : isExpiringSoon
            ? "border-amber-500/40 bg-amber-500/10"
            : "border-border hover:bg-accent/50"
        )}
      >
        <CardContent className="p-4">
          {/* Main Invitation Info */}
          <div className="flex items-start gap-3">
            {/* Avatar - Email based */}
            <div className="relative">
              <Avatar className="h-12 w-12">
                <AvatarFallback className="bg-muted border-2 border-dashed border-muted-foreground/50 text-muted-foreground">
                  {getEmailInitials(member.email)}
                </AvatarFallback>
              </Avatar>

              {/* Pending Status Indicator */}
              <div className="absolute -bottom-1 -right-1">
                <div className="w-4 h-4 rounded-full border-2 border-background flex items-center justify-center bg-amber-400">
                  <Mail className="w-2 h-2 text-background" />
                </div>
              </div>
            </div>

            {/* Invitation Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold truncate text-sm text-foreground">
                  {member.email}
                </h3>
              </div>

              {/* Status Line */}
              <div className="flex items-center gap-2 text-xs mb-2">
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-amber-400">
                  {isExpired ? 'Expired' : 'Pending'} • Invited {formatInvitationDate(member.invited_at)}
                </span>
              </div>

              {/* Role Badge */}
              <div className="flex items-center gap-2 mb-2">
                <Badge variant={roleConfig.variant} className={cn("text-xs", roleConfig.className)}>
                  <RoleIcon className="w-3 h-3 mr-1" />
                  {roleConfig.label}
                </Badge>
              </div>

              {/* Expiration info */}
              <div className="flex items-center gap-2 text-xs">
                <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                <span className={cn(
                  isExpired ? "text-red-400" :
                  isExpiringSoon ? "text-amber-400" : "text-muted-foreground"
                )}>
                  {isExpired ? 'Expired' :
                   daysUntilExpiry === 0 ? 'Expires today' :
                   daysUntilExpiry === 1 ? 'Expires tomorrow' :
                   `Expires in ${daysUntilExpiry} days`}
                </span>
              </div>
            </div>
          </div>

          {/* Floating Action Buttons */}
          {canManageInvitations && (
            <div className="absolute top-3 right-3 flex items-center gap-1">
              {!isExpired && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onResendInvitation?.(member.id);
                  }}
                  className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground"
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onCancelInvitation?.(member.id);
                }}
                className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/20"
              >
                <X className="h-3 w-3" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card
      onClick={onClick}
      className={cn(
        "relative transition-all duration-300",
        onClick ? "cursor-pointer hover:shadow-lg hover:scale-[1.02]" : "cursor-default",
        isSelected
          ? "bg-primary/10 border-primary/30 shadow-lg shadow-primary/20"
          : "hover:bg-accent/50",
        !member.is_active && "opacity-60 bg-muted/30 border-muted-foreground/20"
      )}
    >
      <CardContent className="p-4">
        {/* Main Member Info */}
        <div className="flex items-start gap-3">
          {/* Avatar */}
          <div className="relative">
            <Avatar className={cn(
              "h-12 w-12",
              isSelected && "ring-2 ring-primary/40"
            )}>
              <AvatarFallback className={cn(
                isSelected
                  ? "bg-primary/30 text-primary-foreground"
                  : "bg-muted text-muted-foreground"
              )}>
                {getInitials(member.name)}
              </AvatarFallback>
            </Avatar>

            {/* Online Status Indicator - only show for active members */}
            {member.is_active && (
              <div className="absolute -bottom-1 -right-1">
                <div className={cn(
                  "w-4 h-4 rounded-full border-2 border-background flex items-center justify-center",
                  onlineStatus.color
                )}>
                  <Circle className="w-2 h-2 fill-current" />
                </div>
              </div>
            )}
          </div>

          {/* Member Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h3 className={cn(
                "font-semibold truncate text-sm",
                isSelected ? "text-primary" : "text-foreground"
              )}>
                {member.name}
              </h3>
            </div>

            {/* Email */}
            <div className="flex items-center gap-2 text-xs mb-2">
              <Mail className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="truncate text-muted-foreground">
                {member.email}
              </span>
            </div>

            {/* Role Badge */}
            <div className="flex items-center gap-2 mb-2">
              <Badge variant={roleConfig.variant} className={cn("text-xs", roleConfig.className)}>
                <RoleIcon className="w-3 h-3 mr-1" />
                {roleConfig.label}
              </Badge>
              {!member.is_active && (
                <Badge variant="secondary" className="text-xs bg-red-500/20 text-red-300 border-red-500/30">
                  Inactive
                </Badge>
              )}
            </div>

            {/* Last Seen */}
            <div className="flex items-center gap-2 text-xs">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-muted-foreground">
                Last seen {formatLastSeen(member.last_seen_at)}
              </span>
            </div>
          </div>
        </div>

        {/* Selection Indicator */}
        {isSelected && (
          <div className="absolute top-4 right-4">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamMemberCardShadcn;